/// <amd-module name="Widgets/CumulativeFlowDiagram/CfdAggregationHelper" />
import { Column } from "Analytics/Boards/CumulativeFlowDiagramQueries/BoardColumnsQuery";
import { CFDSamplePoint } from "Analytics/Boards/CumulativeFlowDiagramQueries/CumulativeFlowHistoryQuery";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { daysBetweenDates, parseIsoDateStringInLocalTime } from "WidgetCommon/DateUtils/DateUtils";

/**
 * Calculates the average Work in progress over the period of history provided in @param cumulativeFlowHistory
 * Work in Progress is defined here as all columns from @param selectedColumns that are not the Proposed or Done column.
 * @returns undefined if there are no @param selectedColumns that are not the Proposed or Done column
 */
export function getAverageWip(columns: Column[], cumulativeFlowHistory: CFDSamplePoint[], selectedColumns: string[]): number | undefined {
    const cfDateMap = getFlowHistoryByDate(cumulativeFlowHistory);
    const wipValues: number[] = [];
    const excludedColumnIds = [getProposedColumn(columns).ColumnId, getDoneColumn(columns).ColumnId];
    if (!hasNonExcludedValues(excludedColumnIds, selectedColumns)) {
        return undefined;
    }
    for (const date in cfDateMap) {
        const cumulativeFlowColumnValues = cfDateMap[date];
        const wipForDate = getSumOfColumnValues(cumulativeFlowColumnValues, excludedColumnIds, selectedColumns);
        wipValues.push(wipForDate);
    }
    return Math.round(wipValues.reduce((x, y) => x + y, 0) / (wipValues.length || 1)); // Avoid divide by 0 scenario (the numerator will be 0 in this case).
}

/**
 * Calculates the average Lead Time over the period of history provided in @param cumulativeFlowHistory
 * Lead time is the average time it takes for an item to get from the Proposed column to the done column.
 * This algorithm derives lead time from the cumulative flow history.
 *
 * This algorithm works as follows:
 *      Starting from the right side (latest dates) of the cumulative flow history,
 *      for each date (dateN), get the value for the done column of the board (valueN).
 *
 *      then for each of those dates traverse the data, moving left (backwards in time),
 *      until you find a date where the (done column plus the wip value) (valueM) for that day (dateM) is equal to or less than
 *      valueN. The difference in days from dateM to dateN is the Lead time for dateN.
 *
 *      Repeat this for each date moving back in time until either you do not find a dateM with a sufficient valueM or until
 *      valueN is 0. Then we take the average.
 *
 * Note: the data we have assumes that the proposed and done columns cannot be changed as specified by WIT.
 * @returns undefined if no lead times can be calculated from the dataset.
 */
export function getAverageLeadTime(columns: Column[], cumulativeFlowHistory: CFDSamplePoint[]): number | undefined {
    const cfDateMap = getFlowHistoryByDate(cumulativeFlowHistory);
    const dates = Object.keys(cfDateMap).sort(localeIgnoreCaseComparer);
    const dateToLeadTimeMap: { [key: string]: number } = {};
    for (let i = dates.length - 1; i >= 0; i--) {
        const rightDate = dates[i]; // this is dateN in the above description.
        const rightDateColumnValues = cfDateMap[rightDate];
        const proposedColumn = getProposedColumn(columns);
        const doneColumn = getDoneColumn(columns);
        const doneIndex = rightDateColumnValues.findIndex(x => x.ColumnId === doneColumn.ColumnId);
        if (doneIndex < 0) {
            continue;
        }
        const rightDateDoneValue = rightDateColumnValues[doneIndex].Count;
        for (let j = i - 1; j >= 0; j--) {
            const leftDate = dates[j]; // dateM in the above description.
            const excludedColumns = [proposedColumn.ColumnId];
            const requestedColumns = columns.map(x => x.ColumnId);
            const leftDateColumnValues = cfDateMap[leftDate];
            const leftDateTotalValue = getSumOfColumnValues(leftDateColumnValues, excludedColumns, requestedColumns); // Wip + Done column.
            if (leftDateTotalValue < rightDateDoneValue) {
                dateToLeadTimeMap[rightDate] = daysBetweenDates(
                    parseIsoDateStringInLocalTime(leftDate),
                    parseIsoDateStringInLocalTime(rightDate),
                    true
                );
                break;
            } else if (leftDateTotalValue === rightDateDoneValue) {
                const leftDateDoneColumnValue = getSumOfColumnValues(leftDateColumnValues, excludedColumns, [doneColumn.ColumnId]);
                // If done column didnt change and there was no Wip then lead time is effectively 0.
                const leadTime =
                    leftDateDoneColumnValue === rightDateDoneValue
                        ? 0
                        : daysBetweenDates(parseIsoDateStringInLocalTime(leftDate), parseIsoDateStringInLocalTime(rightDate), true);
                dateToLeadTimeMap[rightDate] = leadTime;
                break;
            }
        }
    }
    const datesWithLeadTimes = Object.keys(dateToLeadTimeMap);
    if (datesWithLeadTimes.length === 0) {
        return undefined;
    }
    return Math.round(datesWithLeadTimes.reduce<number>((total, date) => total + dateToLeadTimeMap[date], 0) / datesWithLeadTimes.length);
}

/**
 * Given Column Data, set of requested columns and excluded columns, gives the total count for the given samplePoints.
 * Note: @param excludedColumnIds takes priority over @param requestedColumnIds
 */
function getSumOfColumnValues(columnData: CFDSamplePoint[], excludedColumnIds: string[], requestedColumnIds: string[]): number {
    const relevantColumns = columnData.filter(x => {
        const isSelected = requestedColumnIds.indexOf(x.ColumnId) >= 0;
        const isNotExcludedColumn = excludedColumnIds.indexOf(x.ColumnId) < 0;
        return isSelected && isNotExcludedColumn;
    });
    return Math.round(relevantColumns.reduce<number>((total, current) => total + current.Count, 0));
}

/**
 * Maps a list of CFD points to a date based map where each value is an array of all of the column data points for that day.
 */
function getFlowHistoryByDate(cumulativeFlowHistory: CFDSamplePoint[]): { [key: string]: CFDSamplePoint[] } {
    const cumulativeFlowHistoryDateMap: { [key: string]: CFDSamplePoint[] } = {};
    cumulativeFlowHistory.forEach(x => {
        if (cumulativeFlowHistoryDateMap[x.SampleDate]) {
            cumulativeFlowHistoryDateMap[x.SampleDate].push(x);
        } else {
            cumulativeFlowHistoryDateMap[x.SampleDate] = [x];
        }
    });
    return cumulativeFlowHistoryDateMap;
}

/**
 * Gets the first active column. This will be the "Proposed" column based on the columns present in @param columnData.
 * @param columns contains order data and column Ids for every column in the board's history.
 */
function getProposedColumn(columns: Column[]): Column {
    return columns.filter(x => !x.IsDeleted).reduce((min, next) => (min.ColumnOrder < next.ColumnOrder ? min : next));
}

/**
 * Gets the last active column. This will be the "Done" column based on the columns present in @param columnData.
 * @param columns contains order data and column Ids for every column in the board's history.
 */
function getDoneColumn(columns: Column[]): Column {
    return columns.filter(x => !x.IsDeleted).reduce((max, next) => (max.ColumnOrder > next.ColumnOrder ? max : next));
}

function hasNonExcludedValues(excludedValues: string[], values: string[]) {
    for (const value of values) {
        if (excludedValues.findIndex(x => x === value) === -1) {
            // If a value supplied is not one of the excluded values.
            return true;
        }
    }
    return false;
}

/**
 * Compares a set of all boards columns in analytics against what the user has selected to determine if the
 * necessary set of columns for lead time calculation has been included.
 * As long as the user has selected all of the columns besides the Proposed column, this will return true.
 * The proposed column is ignored, it doesnt matter if it's included or not.
 */
export function allLeadTimeColumnsSelected(allColumns: Column[], selectedColumns: string[]) {
    const proposedColumn = getProposedColumn(allColumns);
    const filteredColumns = allColumns.filter(x => x.ColumnId !== proposedColumn.ColumnId);
    const filteredIds = filteredColumns.map(x => x.ColumnId).sort();
    const sortedSelectedColumns = selectedColumns.map(x => x).sort();

    return filteredIds.length === sortedSelectedColumns.length && filteredIds.every((value, index) => value === sortedSelectedColumns[index]);
}
